import type { FilterConfig } from '@ibsys/kit';
import type { TimeIntervalFormat } from '@/shared/lib/utils/string';
import { type TimeRangeFilter, getTimeRangeFilter } from '@/shared/lib/utils/timeRangeFilter';
import { ATTRIBUTE_TYPES } from '../constants';
import { type AttributeDisplayInfo, getAttributeTimeFormat } from './getAttributeDisplayName';
import type { ConversationAttributeItem } from '../api/types/api';

/** Конфиг поля времени, которым вводятся границы отбора по атрибуту-интервалу */
export type AttributeTimeRangeFilter = TimeRangeFilter;

/**
 * Поле времени для границ отбора, если значения атрибута показаны как время; `null` — границы
 * вводятся числом (атрибут показан числом секунд либо интервалом не является).
 *
 * Сам вид поля задаёт общее правило отбора-интервала (`getTimeRangeFilter`), здесь к нему лишь
 * добавляется действующий формат показа атрибута. Построители колонок укладывают результат в
 * свой тип конфига (`FilterConfig` у `Table`, `DataGridFilterConfig` у `DataGrid`).
 */
export const getAttributeTimeRangeFilter = (
  attribute: AttributeDisplayInfo | undefined,
  timeDisplayFormat: TimeIntervalFormat,
): AttributeTimeRangeFilter | null => getTimeRangeFilter(getAttributeTimeFormat(attribute, timeDisplayFormat));

/**
 * Конфиг отбора по колонке таблицы: вид ввода выбирается по типу атрибута, а у интервала
 * времени — ещё и по действующему формату его показа.
 */
export const getAttributeColumnFilters = (
  attribute: ConversationAttributeItem,
  timeDisplayFormat: TimeIntervalFormat,
): FilterConfig => {
  if (attribute.dataType === ATTRIBUTE_TYPES.SELECT || attribute.dataType === ATTRIBUTE_TYPES.MULTISELECT) {
    return {
      type: attribute.dataType === ATTRIBUTE_TYPES.MULTISELECT ? ('multiselect' as const) : ('select' as const),
      path: `attributes.${attribute.path}`,
      options:
        attribute.selectValues?.map((option) => ({
          value: option.code,
          label: option.value,
        })) ?? [],
    };
  }

  if (
    attribute.dataType === ATTRIBUTE_TYPES.TIME ||
    attribute.dataType === ATTRIBUTE_TYPES.INTEGER ||
    attribute.dataType === ATTRIBUTE_TYPES.DECIMAL
  ) {
    // Границы отбора вводятся в том же виде, в каком показано значение: интервал времени —
    // полем времени, число секунд — обычным числом (записи числом секунд у `time-range` нет).
    const timeRange = getAttributeTimeRangeFilter(attribute, timeDisplayFormat);
    if (timeRange) {
      return {
        path: `attributes.${attribute.path}`,
        ...timeRange,
      };
    }

    return {
      path: `attributes.${attribute.path}`,
      type: 'integer' as const,
    };
  }

  if (attribute.dataType === ATTRIBUTE_TYPES.DATE) {
    return {
      path: `attributes.${attribute.path}`,
      type: 'date-range' as const,
    };
  }

  if (attribute.dataType === ATTRIBUTE_TYPES.TIMESTAMP) {
    return {
      path: `attributes.${attribute.path}`,
      type: 'timestamp-range' as const,
    };
  }

  if (attribute.dataType === ATTRIBUTE_TYPES.CHECKBOX) {
    return {
      path: `attributes.${attribute.path}`,
      type: 'boolean' as const,
    };
  }

  return {
    path: `attributes.${attribute.path}`,
    type: 'text' as const,
  };
};
